import { createCRUDEntity } from './crud-factory';

// Application-level Equipment type (transformed from DB)
export type EquipmentType = {
  id: string;
  name: string;
  name_translations: unknown;
  brand: string | null;
  model: string | null;
  category: string | null;
  description: string | null;
  description_translations: unknown;
  galleryImages: string[];
  galleryImageAltTexts: unknown[];
  primaryImage: string | null;
  altTextMainPhoto: string | null;
  regulatoryApprovals: unknown[];
  globalLaunchYear: number | null;
  shortTagline: string | null;
  technologyType: string | null;
  keyFeatures: string[];
  koreanName: string | null;
  noInfoFields: unknown[];
  itemType: string;
  slug: string | null;
  wavelengths: string | null;
  createdAt: string | null;
  updatedAt: string | null;
};

type MediaObject = Record<string, unknown>;

const extractMediaUrl = (value: unknown): string | null => {
  if (!value) return null;
  if (typeof value === 'string') {
    const trimmed = value.trim();
    return trimmed || null;
  }

  if (typeof value === 'object') {
    const obj = value as MediaObject;
    // Check for URL in common property names
    const candidates = [
      obj.url,
      obj.publicUrl,
      obj.public_url,
      obj.signedUrl,
      obj.signed_url,
      obj.path,
      obj.file_path,
    ];

    for (const candidate of candidates) {
      if (typeof candidate === 'string' && candidate.trim()) {
        return candidate.trim();
      }
    }
  }

  return null;
};

const toArray = (value: unknown): unknown[] => {
  if (!value) return [];
  if (Array.isArray(value)) return value;
  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (!trimmed) return [];
    try {
      const parsed: unknown = JSON.parse(trimmed);
      if (Array.isArray(parsed)) {
        return parsed;
      }
      if (parsed && typeof parsed === 'object') {
        return Object.values(parsed as Record<string, unknown>);
      }
    } catch {
      return trimmed
        .split(/[\n,;]+/)
        .map(item => item.trim())
        .filter(Boolean);
    }
    return [];
  }
  if (typeof value === 'object') {
    return Object.values(value);
  }
  return [];
};

const normalizeGalleryImages = (value: unknown) => {
  const flattened = toArray(value).flat();
  return [
    ...new Set(
      flattened.map(extractMediaUrl).filter(url => typeof url === 'string' && url.length > 0)
    ),
  ];
};

const deriveMainPhotoUrl = (data: Record<string, unknown>) => {
  if (data.main_photo_url && typeof data.main_photo_url === 'string') {
    const trimmed = data.main_photo_url.trim();
    if (trimmed) {
      return trimmed;
    }
  }
  const galleryImages = normalizeGalleryImages(data.gallery_images);
  return galleryImages.length > 0 ? galleryImages[0] : null;
};

const normalizeGalleryAltTexts = (value: unknown) => {
  const entries = toArray(value);
  return entries
    .map(entry => {
      if (!entry || typeof entry !== 'object') return null;
      const normalized = { ...entry } as Record<string, unknown>;
      const directUrl =
        extractMediaUrl(normalized.url) ??
        extractMediaUrl(normalized.image_url) ??
        extractMediaUrl(normalized.imageUrl);
      if (!normalized.url && directUrl) {
        normalized.url = directUrl;
      }
      if (typeof normalized.alt_texts === 'string' && normalized.alt_texts.trim()) {
        try {
          normalized.alt_texts = JSON.parse(normalized.alt_texts.trim());
        } catch {
          normalized.alt_texts = { en: normalized.alt_texts };
        }
      }
      return normalized;
    })
    .filter(Boolean);
};

const parseKeyFeatures = (
  keyFeatures: unknown
): Array<{ feature: string; subFeatures: unknown }> => {
  if (typeof keyFeatures === 'string' && keyFeatures.trim()) {
    try {
      // Try parsing as JSON first
      const parsed: unknown = JSON.parse(keyFeatures);
      if (Array.isArray(parsed)) {
        return parsed.map(item =>
          typeof item === 'string'
            ? { feature: item, subFeatures: [] }
            : (item as { feature: string; subFeatures: unknown })
        );
      }
      return [];
    } catch {
      // If JSON parse fails, split by newlines or commas and convert to new format
      return keyFeatures
        .split(/[\n,]/)
        .map(f => f.trim())
        .filter(Boolean)
        .map(feature => ({ feature, subFeatures: [] }));
    }
  }
  if (!Array.isArray(keyFeatures)) {
    return keyFeatures
      ? [
          {
            feature: typeof keyFeatures === 'string' ? keyFeatures : JSON.stringify(keyFeatures),
            subFeatures: [],
          },
        ]
      : [];
  }
  // Ensure all features have the correct structure
  return keyFeatures.map(item =>
    typeof item === 'string'
      ? { feature: item, subFeatures: [] }
      : (item as { feature: string; subFeatures: unknown })
  );
};

// Helper function to safely convert values to strings
const safeString = (value: unknown): string => {
  if (value === null || value === undefined) return '';
  if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
    return String(value);
  }
  return ''; // Return empty string for objects/arrays/functions
};

const extractImageData = (dataRecord: Record<string, unknown>) => {
  const galleryImages = normalizeGalleryImages(dataRecord.gallery_images).filter(
    (img): img is string => img !== null
  );
  const galleryImageAltTexts = normalizeGalleryAltTexts(dataRecord.gallery_image_alt_texts);
  const primaryImage = deriveMainPhotoUrl(dataRecord) as string | null;

  return { galleryImages, galleryImageAltTexts, primaryImage };
};

const extractBasicInfo = (dataRecord: Record<string, unknown>) => ({
  id: safeString(dataRecord.id),
  name: safeString(dataRecord.name),
  name_translations: dataRecord.name_translations,
  brand: dataRecord.brand === null ? null : safeString(dataRecord.brand),
  model: dataRecord.model === null ? null : safeString(dataRecord.model),
  category: dataRecord.category === null ? null : safeString(dataRecord.category),
  description: dataRecord.description === null ? null : safeString(dataRecord.description),
  description_translations: dataRecord.description_translations,
});

const extractTechnicalDetails = (
  dataRecord: Record<string, unknown>,
  keyFeatures: Array<{ feature: string; subFeatures: unknown }>
) => ({
  regulatoryApprovals: Array.isArray(dataRecord.regulatory_approvals)
    ? dataRecord.regulatory_approvals
    : [],
  keyFeatures: keyFeatures.map(kf => kf.feature),
  technologyType:
    dataRecord.technology_type === null ? null : safeString(dataRecord.technology_type),
  wavelengths: dataRecord.wavelengths === null ? null : safeString(dataRecord.wavelengths),
  globalLaunchYear:
    dataRecord.global_launch_year === null
      ? null
      : typeof dataRecord.global_launch_year === 'number'
        ? dataRecord.global_launch_year
        : null,
});

const extractMetadata = (dataRecord: Record<string, unknown>) => ({
  koreanName: dataRecord.korean_name === null ? null : safeString(dataRecord.korean_name),
  shortTagline: dataRecord.short_tagline === null ? null : safeString(dataRecord.short_tagline),
  noInfoFields: Array.isArray(dataRecord.no_info_fields) ? dataRecord.no_info_fields : [],
  itemType:
    dataRecord.item_type === null ? 'equipment' : safeString(dataRecord.item_type) || 'equipment',
  slug: dataRecord.slug === null ? null : safeString(dataRecord.slug),
  createdAt: dataRecord.created_at === null ? null : safeString(dataRecord.created_at),
  updatedAt: dataRecord.updated_at === null ? null : safeString(dataRecord.updated_at),
});

const transformFromDB = (data: unknown): EquipmentType => {
  if (!data) {
    throw new Error('Cannot transform null/undefined data');
  }

  const dataRecord = { ...(data as Record<string, unknown>) };
  const keyFeatures = parseKeyFeatures(dataRecord.key_features);
  const imageData = extractImageData(dataRecord);

  return {
    ...extractBasicInfo(dataRecord),
    ...extractTechnicalDetails(dataRecord, keyFeatures),
    ...imageData,
    altTextMainPhoto: null,
    ...extractMetadata(dataRecord),
  };
};

const normalizeNumber = (value: unknown) => {
  if (value === undefined || value === null) return null;
  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (!trimmed) return null;
    const parsed = Number(trimmed);
    return Number.isNaN(parsed) ? null : parsed;
  }
  if (typeof value === 'number') {
    return Number.isNaN(value) ? null : value;
  }
  return null;
};

const transformToDB = (data: unknown) => {
  const dataRecord = data as Record<string, unknown>;
  const galleryImages = normalizeGalleryImages(
    dataRecord.galleryImages ?? dataRecord.gallery_images
  );
  const mergedGalleryImages = [dataRecord.mainPhotoUrl as string, ...galleryImages]
    .filter((value: unknown) => typeof value === 'string' && value.trim().length > 0)
    .map((value: unknown) => (value as string).trim());

  const dbData = {
    name: dataRecord.name,
    brand: dataRecord.brand,
    model: dataRecord.model,
    category: dataRecord.category,
    short_tagline: dataRecord.shortTagline,
    description: dataRecord.description,
    technology_type: dataRecord.technologyType,
    wavelengths: dataRecord.wavelengths,
    // Store key_features as JSON string if it's an array
    key_features: Array.isArray(dataRecord.keyFeatures)
      ? JSON.stringify(dataRecord.keyFeatures)
      : dataRecord.keyFeatures,
    regulatory_approvals: dataRecord.regulatoryApprovals,
    global_launch_year: normalizeNumber(dataRecord.globalLaunchYear),
    korean_name: dataRecord.koreanName,
    no_info_fields: dataRecord.noInfoFields,
    item_type: dataRecord.itemType ?? 'equipment',
    slug: dataRecord.slug,
    gallery_images: [...new Set(mergedGalleryImages)],
    gallery_image_alt_texts: normalizeGalleryAltTexts(
      dataRecord.galleryImageAltTexts ?? dataRecord.gallery_image_alt_texts
    ),
  };

  // Remove undefined values
  return Object.fromEntries(Object.entries(dbData).filter(([, v]) => v !== undefined));
};

const transformConfig = {
  transformFromDB,
  transformToDB,
};

export const Equipment = createCRUDEntity('equipment', transformConfig);
export { transformFromDB as transformEquipmentFromDB };
export default Equipment;
